import {
	createFileRoute,
	useNavigate,
	useParams,
	useRouteContext,
} from "@tanstack/react-router";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { toast } from "sonner";
import { Heading } from "@/components/heading";
import { OfferForm, type OfferFormValues } from "@/forms/offer-form";
import { PaymentProviderInfo } from "@/utils/payment-provider-info";
import type { integracoesCheckoutProdutoIdMutation } from "./__generated__/integracoesCheckoutProdutoIdMutation.graphql";
import type { integracoesCheckoutProdutoIdQuery } from "./__generated__/integracoesCheckoutProdutoIdQuery.graphql";

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/configuracoes/integracoes/checkout/produto/$id/",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const { id } = useParams({ from: Route.id });
	const navigate = useNavigate();

	const data = useLazyLoadQuery<integracoesCheckoutProdutoIdQuery>(
		graphql`
		query integracoesCheckoutProdutoIdQuery($slug: String!, $id: ID!) {
			club(slug: $slug) {
				integrations {
					... on PaymentIntegration {
						id
						provider
					}
					__typename
				}

				spaces {
					id
					name
					icon
					group {
						name
					}
				}

				courses(first: 1000) {
					edges {
						node {
							id
							name
							category {
								id
								name
							}
							memberGroups {
								id
								name
								isDefault
							}
						}
					}
				}

				tags(first: 1000) {
					edges {
						node {
							id
							text
						}
					}
				}

				badges(first: 1000) {
					edges {
						node {
							id
							...memberBadgeFragment
						}
					}
				}
			}

			node(id: $id) {
				... on PaymentIntegrationProduct {
					id
					enabled
					productName
					productId
					basePrice
					checkoutUrl
					description
					accessDefaultDuration
					integration {
						id
					}
					accessGroup {
						accessGroup {
							spaces {
								id
							}
							courses {
								course {
									id
								}
								memberGroup {
									id
								}
							}
							tags {
								id
							}
							badges {
								id
							}
						}
					}
				}
			}
		}
		`,
		{ slug: club.slug, id },
	);

	const [commitMutation, isSubmitting] =
		useMutation<integracoesCheckoutProdutoIdMutation>(
			graphql`
			mutation integracoesCheckoutProdutoIdMutation(
				$input: PaymentIntegrationProductUpdateInput!
			) {
				paymentIntegrationProductUpdate(
					paymentIntegrationProductInput: $input
				) {
					id
					integration {
						id
					}
				}
			}
		`,
		);

	const product = data.node;

	const integrations = data.club.integrations
		.filter(
			(
				it,
			): it is {
				readonly __typename: "PaymentIntegration";
				readonly id: string;
				readonly provider: keyof typeof PaymentProviderInfo;
			} =>
				it.__typename === "PaymentIntegration" &&
				it.provider !== "%future added value",
		)
		.map((it) => ({
			id: it.id,
			provider: it.provider,
			providerName: PaymentProviderInfo[it.provider]?.name || it.provider,
		}));

	const feeds = data.club.spaces.map((space) => ({
		id: space.id,
		name: space.name,
		icon: space.icon ?? null,
		groupName: space.group?.name,
	}));

	const courses = data.club.courses.edges.map(({ node: course }) => ({
		id: course.id,
		name: course.name,
		category: course.category,
		memberGroups: course.memberGroups.map((group) => ({
			id: group.id,
			name: group.name,
			isDefault: group.isDefault,
		})),
	}));

	const tags = data.club.tags.edges.map(({ node: tag }) => ({
		id: tag.id,
		text: tag.text,
	}));

	const badges = data.club.badges.edges.map(({ node: badge }) => badge);

	// Build default values from existing product
	const defaultValues: Partial<OfferFormValues> | undefined = product
		? {
				type: product.integration ? "checkout" : "management",
				name: product.productName || "",
				basePrice: product.basePrice || "",
				description: product.description || null,
				integrationId: product.integration?.id || null,
				productId: product.productId || "",
				checkoutUrl: product.checkoutUrl || "",
				expirationDays: product.accessDefaultDuration || null,
				feeds:
					product.accessGroup?.accessGroup?.spaces
						.filter(Boolean)
						?.map((s) => s.id) || [],
				courses:
					product.accessGroup?.accessGroup?.courses
						.filter(Boolean)
						?.map((c) => ({
							id: c.course.id,
							memberGroup: c.memberGroup?.id || "",
						})) || [],
				tags: product.accessGroup?.accessGroup?.tags?.map((t) => t.id) || [],
				badges:
					product.accessGroup?.accessGroup?.badges?.map((b) => b.id) || [],
			}
		: undefined;

	const handleSubmit = (values: OfferFormValues) => {
		commitMutation({
			variables: {
				input: {
					id,
					productName: values.name,
					productId: values.productId || undefined,
					basePrice: values.basePrice || undefined,
					checkoutUrl: values.checkoutUrl || undefined,
					description: values.description,
					accessDefaultDuration: values.expirationDays || undefined,
					enabled: product?.enabled ?? true,
					accessgroup: {
						spaces: values.feeds,
						courses: values.courses.map((c) => ({
							id: c.id,
							memberGroup: c.memberGroup || undefined,
						})),
						tags: values.tags.length > 0 ? values.tags : undefined,
						badges: values.badges.length > 0 ? values.badges : undefined,
					},
				},
			},
			onCompleted: (response, errors) => {
				if (errors?.length) {
					toast.error(errors[0].message);
					return;
				}
				toast.success("Oferta atualizada com sucesso");
				const integrationId =
					response.paymentIntegrationProductUpdate.integration?.id;

				if (integrationId) {
					navigate({
						to: "/c/$club/configuracoes/integracoes/checkout/$id",
						params: { club: club.slug, id: integrationId },
					});
				} else {
					navigate({
						to: "/c/$club/monetizacao/ofertas",
						params: { club: club.slug },
					});
				}
			},
			onError: (error) => {
				toast.error("Erro ao atualizar oferta", {
					description: error.message,
				});
			},
		});
	};

	if (!product) {
		return (
			<div className="size-full flex items-start justify-center py-4 md:py-8">
				<div className="p-6 w-full max-md:px-4 max-w-[700px] flex flex-col gap-6 border rounded-md">
					<Heading>Oferta não encontrada</Heading>
					<p className="text-muted-foreground">
						A oferta solicitada não foi encontrada ou não está disponível.
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="size-full flex items-start justify-center py-4 md:py-8">
			<div className="p-6 w-full max-md:px-4 max-w-[700px] flex flex-col gap-6 border rounded-md">
				<Heading>Editar Oferta</Heading>

				<OfferForm
					onSubmit={handleSubmit}
					integrations={integrations}
					feeds={feeds}
					courses={courses}
					tags={tags}
					badges={badges}
					defaultValues={defaultValues}
					lockedIntegrationId={product.integration?.id}
					isSubmitting={isSubmitting}
				/>
			</div>
		</div>
	);
}
